uniform float uTime;
uniform sampler2D uTrailTexture;
uniform vec2 uResolution;
uniform float uAspectRatio;
uniform float uMouseRadius;

varying vec2 vUv;
varying vec2 vDisplacedUv;
varying float vDistortionStrength;

// Simplex noise functions
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }

float snoise(vec2 v) {
    const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
    vec2 i = floor(v + dot(v, C.yy));
    vec2 x0 = v - i + dot(i, C.xx);
    vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
    vec2 x1 = x0.xy + C.xx - i1;
    vec2 x2 = x0.xy + C.zz;
    i = mod289(i);
    vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
    vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x1,x1), dot(x2,x2)), 0.0);
    m = m*m;
    m = m*m;
    vec3 x = 2.0 * fract(p * C.www) - 1.0;
    vec3 h = abs(x) - 0.5;
    vec3 ox = floor(x + 0.5);
    vec3 a0 = x - ox;
    m *= 1.79284291400159 - 0.85373472095314 * (a0*a0+h*h);
    vec3 g = vec3(0.0);
    g.x = a0.x * x0.x + h.x * x0.y;
    g.yz = a0.yz * vec2(x1.x,x2.x) + h.yz * vec2(x1.y,x2.y);
    return 130.0 * dot(m, g);
}

void main() {
    // Base color (black) - minimized to near-zero
    vec3 baseColor = vec3(0.0, 0.0, 0.0);

    // Distortion color (DC0000 red)
    vec3 distortionColor = vec3(0.863, 0.0, 0.0);

    // Secondary color (complementary to red, e.g., cyan/blue)
    vec3 secondaryColor = vec3(0.2, 0.7, 0.9); // Brighter cyan

    // Sample the trail texture to get the base intensity
    vec4 trailData = texture2D(uTrailTexture, vDisplacedUv);
    float trailIntensity = trailData.r;

    // Create a wobbly shape using noise for the cursor
    float noiseX = snoise(vDisplacedUv * 15.0 + uTime * 0.7) * 0.5 + 0.5;
    float noiseY = snoise(vDisplacedUv * 15.0 + uTime * 0.7 + 100.0) * 0.5 + 0.5;
    vec2 wobblyOffset = vec2(noiseX - 0.5, noiseY - 0.5) * 0.5; // Strong wobble

    // Distort UVs to create a wobbly shape
    vec2 wobblyUV = vDisplacedUv + wobblyOffset;
    vec4 wobblyTrailData = texture2D(uTrailTexture, wobblyUV);
    float wobblyIntensity = wobblyTrailData.r;

    // Define the base radius with wobbly distortion
    vec2 centeredUv = vDisplacedUv - 0.5;
    float baseRadius = uMouseRadius * 0.5;
    float distortedRadius = length(centeredUv + wobblyOffset * 0.7);

    // Use the wobbly shape for intensity with almost no masking
    float wobblyShapeIntensity = 1.0 - smoothstep(baseRadius * 0.99, baseRadius * 1.01, distortedRadius); // Ultra-tight range

    // Combine the wobbly shape with trail intensity
    float combinedIntensity = wobblyIntensity * wobblyShapeIntensity * 2.5; // Further boost intensity

    // Add noise for fluid variation
    float noiseVariation = snoise(wobblyUV * 6.0 + uTime * 0.5) * 0.5 + 0.5;
    float fluidIntensity = combinedIntensity + (noiseVariation - 0.5) * 0.2;

    // Combine with distortion strength
    float finalIntensity = vDistortionStrength * clamp(fluidIntensity, 0.0, 2.5); // Higher intensity cap

    // Create a near-zero transition for blending
    float alpha = smoothstep(0.001, 0.1, finalIntensity); // Near-zero range to eliminate mask

    // Blend between secondary color and distortion color
    vec3 colorMix = mix(secondaryColor, distortionColor, clamp(finalIntensity, 0.0, 1.0) * 3.0); // Even stronger red dominance

    // Final color with minimal background influence
    vec3 finalColor = mix(baseColor * 0.01, colorMix, alpha * 0.99); // Almost no base color influence

    gl_FragColor = vec4(finalColor, 1.0); // Full opacity for color blending
}